"use client";

import { MenuItem } from "@noya-app/noya-designsystem";
import {
  CreateResourceParametersWithoutAccessibleByFileId,
  DeleteResourceParameters,
  diffResourceMaps,
  Input,
  MediaItem,
  ModifiedResource,
  OutputTransform,
  Resource,
  ResourceCreate,
  ResourceCreateMap,
  ResourceMap,
} from "@noya-app/noya-schemas";
import {
  GetAtPath,
  Observable,
  ObservableOptions,
  PathKey,
} from "@noya-app/observable";
import { useJsonMemo, useStableCallback } from "@noya-app/react-utils";
import {
  ActivityEventsManager,
  AssetTranscriptionResult,
  CreateParams,
  ExtractRequestBody,
  findNoyaUser,
  MultiplayerPatchMetadata,
  MultiplayerStateManager,
  NoyaManager,
  PublishCallback,
  ResourceManager,
  SafeEval,
  SharedConnectionDataManager,
  StateManagerOptions,
  Static,
  StreamFilter,
  TranscriptionManager,
  TSchema,
} from "@noya-app/state-manager";
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AppTheme,
  AppViewType,
  useNoyaStateInternal,
  UseNoyaStateOptions,
} from "./noyaApp";
import { getNoyaManagerSingleton, getNoyaSingletonBindings } from "./singleton";
import { useObservable } from "./useObservable";

export function resourceToCreateParameters(
  resource: ResourceCreate
): CreateResourceParametersWithoutAccessibleByFileId {
  switch (resource.type) {
    case "directory":
      return {
        path: resource.path,
        type: "directory",
      };
    case "asset":
      return {
        path: resource.path,
        type: "asset",
        assetId: resource.assetId,
        asset: resource.asset,
      };
    case "file":
      return {
        path: resource.path,
        type: "file",
        fileId: resource.fileId,
      };
    case "resource":
      return {
        path: resource.path,
        type: "resource",
        fileId: resource.fileId,
        resourceId: resource.resourceId,
      };
    default:
      throw new Error(`Unknown resource type: ${(resource as any).type}`);
  }
}

type ResourcePatchRequestBody = ExtractRequestBody<"PATCH /api/resources">;

type BuildResourcePatchPayloadParameters = {
  addedResources: ResourceCreate[];
  modifiedResources: ModifiedResource[];
  removedResources: Resource[];
};

export function buildResourcePatchPayload({
  addedResources,
  modifiedResources,
  removedResources,
}: BuildResourcePatchPayloadParameters): ResourcePatchRequestBody {
  const createEntries = addedResources.map(resourceToCreateParameters);

  const dedupedCreateEntries = Array.from(
    new Map(createEntries.map((entry) => [entry.path, entry])).values()
  );

  const updateEntries =
    modifiedResources.map((resource) => {
      const update: NonNullable<ResourcePatchRequestBody["update"]>[number] = {
        id: resource.id,
        ...(resource.path !== undefined ? { path: resource.path } : {}),
        ...(resource.assetId !== undefined
          ? { assetId: resource.assetId }
          : {}),
        ...(resource.asset ? { asset: resource.asset } : {}),
      };

      return update;
    }) ?? [];

  const deleteEntries: DeleteResourceParameters[] = removedResources.map(
    (resource) => ({ id: resource.id })
  );

  const patch: ResourcePatchRequestBody = {};

  if (dedupedCreateEntries.length) {
    patch.create = dedupedCreateEntries;
  }
  if (updateEntries.length) {
    patch.update = updateEntries;
  }
  if (deleteEntries.length) {
    patch.delete = deleteEntries;
  }

  return patch;
}

export interface NoyaStateProviderProps<
  S,
  M extends object,
  E extends object,
  MenuT extends string,
  I extends Record<string, any> = Record<string, unknown>,
> extends Omit<
    UseNoyaStateOptions<S, M, E, MenuT, I>,
    "schema" | "mergeHistoryEntries"
  > {
  children: React.ReactNode;
  initialState?: S;
  fallback?: React.ReactNode;
}

export type AnyNoyaStateContextValue = {
  noyaManager: NoyaManager<any, any, any, any, any>;
  theme: AppTheme;
  viewType: AppViewType;
};

export const AnyNoyaStateContext = createContext<
  AnyNoyaStateContextValue | undefined
>(undefined);

export function useAnyNoyaStateContext() {
  const value = useContext(AnyNoyaStateContext);

  if (!value) {
    const bindings = getNoyaSingletonBindings();

    if (bindings) {
      return {
        noyaManager: getNoyaManagerSingleton(),
        theme: bindings.theme,
        viewType: bindings.viewType,
      } as AnyNoyaStateContextValue;
    }

    throw new Error(
      "useNoyaStateContext must be used within a NoyaStateProvider"
    );
  }

  return value;
}

export function useAnyNoyaManager() {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager as NoyaManager<any, any, any, any, any>;
}

export function useColorScheme() {
  const { theme } = useAnyNoyaStateContext();
  return theme;
}

export function useViewType() {
  const { viewType } = useAnyNoyaStateContext();
  return viewType;
}

export function useAssets() {
  const { noyaManager } = useAnyNoyaStateContext();
  return useObservable(noyaManager.assetManager.assets$);
}

export function useAsset(id: string | undefined) {
  const { noyaManager } = useAnyNoyaStateContext();
  const observable = useMemo(
    () =>
      noyaManager.assetManager.assets$.map(
        (assets) =>
          assets.find((a) => a.stableId === id) ??
          assets.find((a) => a.id === id)
      ),
    [noyaManager, id]
  );
  return useObservable(observable);
}

export function useAssetManager() {
  const { noyaManager } = useAnyNoyaStateContext();
  const isInitialized = useObservable(noyaManager.assetManager.isInitialized$);
  return useMemo(
    () => ({
      create: noyaManager.assetManager.create,
      delete: noyaManager.assetManager.delete,
      isInitialized,
    }),
    [noyaManager, isInitialized]
  );
}

export function useAIManager() {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager.aiManager;
}

export function useSecretManager() {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager.secretManager;
}

export function useSecrets() {
  const { noyaManager } = useAnyNoyaStateContext();
  return useObservable(noyaManager.secretManager.secrets$);
}

export function useSecret(name: string) {
  const { noyaManager } = useAnyNoyaStateContext();

  return useObservable(
    noyaManager.secretManager.secrets$,
    useCallback((secrets) => secrets.find((s) => s.name === name), [name])
  );
}

export function useInputs() {
  const { noyaManager } = useAnyNoyaStateContext();
  return useObservable(noyaManager.ioManager.inputs$);
}

export function useOutputTransforms() {
  const { noyaManager } = useAnyNoyaStateContext();
  return useObservable(noyaManager.ioManager.outputTransforms$);
}

export function useIOManager() {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager.ioManager;
}

export function usePublish(callback: PublishCallback) {
  const { noyaManager } = useAnyNoyaStateContext();
  const stableCallback = useStableCallback(callback);

  useEffect(() => {
    noyaManager.publishingManager.setPublishingEnabled(true);
    noyaManager.publishingManager.getFilesToPublish = stableCallback;

    return () => {
      noyaManager.publishingManager.setPublishingEnabled(false);
      noyaManager.publishingManager.getFilesToPublish = undefined;
    };
  }, [noyaManager, stableCallback]);
}

export function useIsInitialized() {
  const { noyaManager, viewType } = useAnyNoyaStateContext();

  const isInitializedObservable = useMemo(
    () =>
      Observable.combine(
        [
          ...(viewType === "preview"
            ? []
            : [noyaManager.multiplayerStateManager.isInitialized$]),
          noyaManager.secretManager.isInitialized$,
          noyaManager.assetManager.isInitialized$,
        ],
        (values) => values.every((v) => v)
      ),
    [noyaManager, viewType]
  );

  return useObservable(isInitializedObservable);
}

export function usePipelineManager() {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager.pipelineManager;
}

export function useScriptEvaluator() {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager.scriptEvaluatorManager;
}

export function useNoyaUserManager() {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager.userManager;
}

export function useNoyaUsers() {
  const userManager = useNoyaUserManager();
  return useObservable(userManager?.allUsers$);
}

export function useNoyaUser(id?: string) {
  const userManager = useNoyaUserManager();

  return useObservable(
    userManager?.allUsers$,
    useCallback((users) => findNoyaUser(users, id), [id])
  );
}

export function useNoyaActiveUsers() {
  const userManager = useNoyaUserManager();
  return useObservable(userManager?.connectedUsers$);
}

export function useNoyaCurrentUser() {
  const userManager = useNoyaUserManager();
  return useObservable(userManager?.currentUser$);
}

export function useNoyaCurrentUserId() {
  const userManager = useNoyaUserManager();
  return useObservable(userManager?.currentId$);
}

export function useAnySharedConnectionData() {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager.sharedConnectionDataManager;
}

export function useEnqueueInput<
  I extends Record<string, any> = Record<string, unknown>,
>() {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager.enqueueInput as NoyaManager<
    any,
    any,
    any,
    any,
    I
  >["enqueueInput"];
}

export function useIsProcessing() {
  const { noyaManager } = useAnyNoyaStateContext();
  return useObservable(noyaManager.isProcessing$);
}

export function useFileName() {
  const { noyaManager } = useAnyNoyaStateContext();
  return useObservable(noyaManager.filePropertyManager.optimisticValue$);
}

export function useFilePropertyManager() {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager.filePropertyManager;
}

export function useSetFileNameState() {
  const fileName = useFileName();
  const filePropertyManager = useFilePropertyManager();
  return [fileName, filePropertyManager.updateName] as const;
}

export function useResourceManager(): ResourceManager {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager.resourceManager;
}

export function useNoyaTranscriptionManager(): TranscriptionManager {
  const { noyaManager } = useAnyNoyaStateContext();
  return noyaManager.transcriptionManager;
}

type UseNoyaAssetTranscriptionOptions = {
  endpoint?: string;
  apiKey?: string;
};

/**
 * Returns a text transcription for the given assetId.
 *
 * - This API is available when running your app on noya.io, not when running it locally.
 * - An asset must first be uploaded via the `useAssetManager().create()` method before
 *   it can be transcribed via this API.
 * - The asset must be a valid audio or video file.
 */
export function useNoyaAssetTranscription(
  assetId: string,
  options?: UseNoyaAssetTranscriptionOptions
): AssetTranscriptionResult;
export function useNoyaAssetTranscription(
  assetId?: string,
  options?: UseNoyaAssetTranscriptionOptions
): AssetTranscriptionResult | undefined;
export function useNoyaAssetTranscription(
  assetId?: string,
  options?: UseNoyaAssetTranscriptionOptions
): AssetTranscriptionResult | undefined {
  const transcriptionManager = useNoyaTranscriptionManager();

  const observable = useMemo(() => {
    if (!assetId) return undefined;

    return transcriptionManager.getAssetTranscription$(assetId, {
      endpoint: options?.endpoint,
      apiKey: options?.apiKey,
    });
  }, [transcriptionManager, assetId, options?.endpoint, options?.apiKey]);

  return useObservable(observable);
}

// export function useResources(): Resource[] {
//   const { noyaManager } = useAnyNoyaStateContext();
//   const resources = useObservable(noyaManager.resourceManager.resources$);
//   return resources;
// }

export function resourceToMediaItem(resource: Resource): MediaItem {
  switch (resource.type) {
    case "asset":
      return { kind: "asset", id: resource.id, assetId: resource.assetId };
    case "file":
      return { kind: "noyaFile", id: resource.id, fileId: resource.fileId };
    default:
      return { kind: "folder", id: resource.id };
  }
}

export function useResources({
  shouldDeleteAssets = true,
}: {
  shouldDeleteAssets?: boolean;
} = {}): [ResourceMap, Dispatch<SetStateAction<ResourceCreateMap>>] {
  const resourceManager = useResourceManager();
  const assetManager = useAssetManager();

  const resourceMap = useObservable(
    resourceManager.optimisticResources$,
    useCallback((resources) => {
      return Object.fromEntries(
        resources.map((resource) => [resource.path, resource])
      );
    }, [])
  );

  const setResourceMap = useCallback(
    async (action: SetStateAction<ResourceCreateMap>) => {
      const newResourceMap =
        action instanceof Function ? action(resourceMap) : action;

      const { addedResources, removedResources, modifiedResources } =
        diffResourceMaps(resourceMap, newResourceMap, "id");

      const patchPayload = buildResourcePatchPayload({
        addedResources,
        modifiedResources,
        removedResources,
      });

      const hasPatchOperations =
        (patchPayload.create?.length ?? 0) > 0 ||
        (patchPayload.update?.length ?? 0) > 0 ||
        (patchPayload.delete?.length ?? 0) > 0;

      if (hasPatchOperations) {
        await resourceManager.patchResources(patchPayload);
      }

      if (shouldDeleteAssets) {
        for (const resource of removedResources) {
          if (resource.type === "asset") {
            await assetManager.delete(resource.assetId);
          }
        }
      }
    },
    [resourceMap, resourceManager, shouldDeleteAssets, assetManager]
  );

  return [resourceMap, setResourceMap];
}

const emptyArray: unknown[] = [];

export function useActivityEvents(streamFilter: StreamFilter) {
  const { noyaManager } = useAnyNoyaStateContext();
  return useActivityEventsForManager(
    streamFilter,
    noyaManager.activityEventsManager
  );
}

export function useActivityEventsForManager(
  streamFilter: StreamFilter | undefined,
  activityEventsManager: ActivityEventsManager
) {
  const [streamId, setStreamId] = useState<string | undefined>(undefined);
  const stableStreamFilter = useJsonMemo(streamFilter);

  useEffect(() => {
    if (!stableStreamFilter) return;

    const streamId = activityEventsManager.subscribe(stableStreamFilter);

    setStreamId(streamId);

    return () => {
      setStreamId(undefined);

      activityEventsManager.unsubscribe(streamId);
    };
  }, [activityEventsManager, stableStreamFilter]);

  const observable = streamId
    ? activityEventsManager.getStream(streamId)
    : undefined;

  return useObservable(observable) ?? emptyArray;
}

export const FallbackUntilInitialized = ({
  children,
  fallback,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) => {
  const isInitialized = useIsInitialized();

  return isInitialized ? children : fallback ?? null;
};

export function createNoyaContext<
  Schema extends TSchema,
  M extends object = object,
  E extends object = object,
  MenuT extends string = string,
  I extends Record<string, any> = Record<string, unknown>,
>({
  schema,
  mergeHistoryEntries,
  outputTransforms,
  inputs,
  safeEval,
}: {
  schema: Schema;
  mergeHistoryEntries?: StateManagerOptions<
    Static<Schema>,
    M & Pick<MultiplayerPatchMetadata, "name" | "timestamp">
  >["mergeHistoryEntries"];
  safeEval?: SafeEval;
  outputTransforms?: CreateParams<OutputTransform>[];
  inputs?: CreateParams<Input>[];
}) {
  type S = Static<Schema>;
  type PartialM = Partial<
    M & Pick<MultiplayerPatchMetadata, "name" | "timestamp">
  >;

  const NoyaStateContext = AnyNoyaStateContext as React.Context<
    NoyaManager<S, M, E, MenuT, I> | undefined
  >;

  function NoyaContextProvider({
    children,
    contextValue,
  }: {
    children: React.ReactNode;
    contextValue: AnyNoyaStateContextValue;
  }) {
    return (
      <AnyNoyaStateContext.Provider value={contextValue}>
        {children}
      </AnyNoyaStateContext.Provider>
    );
  }

  function NoyaStateProvider({
    children,
    initialState,
    ...options
  }: NoyaStateProviderProps<S, M, E, MenuT, I>) {
    const [, , { noyaManager, theme, viewType }] = useNoyaStateInternal<
      S,
      M,
      E,
      MenuT,
      I
    >(initialState, {
      schema,
      mergeHistoryEntries,
      outputTransforms,
      inputs,
      safeEval,
      ...(options as any),
    });

    const contextValue = useMemo(
      () => ({
        noyaManager,
        theme,
        viewType,
      }),
      [noyaManager, theme, viewType]
    );

    return (
      <NoyaContextProvider contextValue={contextValue}>
        {options.fallback !== undefined ? (
          <FallbackUntilInitialized fallback={options.fallback}>
            {children}
          </FallbackUntilInitialized>
        ) : (
          children
        )}
      </NoyaContextProvider>
    );
  }

  function useValue(): S;
  function useValue<A>(
    selector: (value: S) => A,
    options?: Pick<ObservableOptions, "isEqual">
  ): A;
  function useValue<P extends PathKey[] | string>(path: P): GetAtPath<S, P>;
  function useValue(
    path?: PathKey[] | string | ((value: S) => any),
    options?: Pick<ObservableOptions, "isEqual">
  ) {
    const { noyaManager } = useAnyNoyaStateContext();

    let actualPath: PathKey[] | string =
      typeof path === "function" || !path ? "" : path;

    const mappedObservable = useMemo(() => {
      if (typeof path === "function") {
        return noyaManager.multiplayerStateManager.optimisticState$.map(path, {
          isEqual: options?.isEqual,
        });
      }
      return noyaManager.multiplayerStateManager.optimisticState$;
    }, [noyaManager, path, options?.isEqual]);

    const value = useObservable(mappedObservable, actualPath);

    return value;
  }

  function useSetValue(): (
    ...args:
      | [metadata: PartialM, value: SetStateAction<S>]
      | [value: SetStateAction<S>]
  ) => void;
  function useSetValue<P extends PathKey[] | string>(
    path: P
  ): (
    ...args:
      | [metadata: PartialM, value: SetStateAction<GetAtPath<S, P>>]
      | [value: SetStateAction<GetAtPath<S, P>>]
  ) => void;
  function useSetValue<P extends PathKey[] | string>(path?: P) {
    const { noyaManager } = useAnyNoyaStateContext();

    const setValue = useCallback(
      (
        ...args:
          | [metadata: PartialM, value: SetStateAction<GetAtPath<S, P>>]
          | [value: SetStateAction<GetAtPath<S, P>>]
      ): void => {
        const [metadata, value] = args.length === 2 ? args : [{}, args[0]];

        noyaManager.multiplayerStateManager.setStateAtPath(
          metadata,
          (path ?? "") as P,
          value as never
        );
      },
      [noyaManager, path]
    );

    return setValue;
  }

  function useValueState(): [
    S,
    (
      ...args:
        | [metadata: PartialM, value: SetStateAction<S>]
        | [value: SetStateAction<S>]
    ) => void,
  ];
  function useValueState<A>(
    selector: (value: S) => A,
    options?: ObservableOptions
  ): [
    A,
    (
      ...args:
        | [metadata: PartialM, value: SetStateAction<S>]
        | [value: SetStateAction<S>]
    ) => void,
  ];
  function useValueState<P extends PathKey[] | string>(
    path?: P
  ): [
    GetAtPath<S, P>,
    (
      ...args:
        | [metadata: PartialM, value: SetStateAction<GetAtPath<S, P>>]
        | [value: SetStateAction<GetAtPath<S, P>>]
    ) => void,
  ];
  function useValueState<P extends PathKey[] | string>(
    ...args: [P?] | [selector: (value: S) => any, options?: ObservableOptions]
  ) {
    const [selector, options, path] =
      args.length >= 1 && typeof args[0] === "function"
        ? [args[0], args[1], undefined]
        : [undefined, undefined, args[0] as P | undefined];

    const value = useValue((selector ?? path ?? "") as any, options);
    const setValue = useSetValue(path ?? "");
    return [value, setValue];
  }

  function useStateManager() {
    const { noyaManager } = useAnyNoyaStateContext();

    return noyaManager.multiplayerStateManager as MultiplayerStateManager<
      S,
      PartialM
    >;
  }

  function useSharedConnectionDataManager() {
    const { noyaManager } = useAnyNoyaStateContext();
    return noyaManager.sharedConnectionDataManager as SharedConnectionDataManager<E>;
  }

  function useNoyaManager() {
    return useAnyNoyaStateContext().noyaManager as NoyaManager<
      S,
      PartialM,
      E,
      MenuT,
      I
    >;
  }

  function useLeftMenuItems() {
    const { menuManager } = useNoyaManager();

    return useObservable(menuManager.leftMenuItems$);
  }

  function useSetLeftMenuItems(leftMenuItems?: MenuItem<MenuT>[]) {
    const { menuManager } = useNoyaManager();

    useEffect(() => {
      if (leftMenuItems) {
        menuManager.setLeftMenuItems(leftMenuItems);
      }
    }, [menuManager, leftMenuItems]);
  }

  function useRightMenuItems() {
    const { menuManager } = useNoyaManager();

    return useObservable(menuManager.rightMenuItems$);
  }

  function useSetRightMenuItems(rightMenuItems?: MenuItem<MenuT>[]) {
    const { menuManager } = useNoyaManager();

    useEffect(() => {
      if (rightMenuItems) {
        menuManager.setRightMenuItems(rightMenuItems);
      }
    }, [menuManager, rightMenuItems]);
  }

  function useHandleMenuItem(callback: (type: MenuT) => void) {
    const { menuManager } = useNoyaManager();

    useEffect(() => {
      return menuManager.addListener(callback);
    }, [menuManager, callback]);
  }

  function useOnSelectMenuItemCallback(): (type: MenuT) => void {
    const { menuManager } = useNoyaManager();

    return useCallback((type: MenuT) => menuManager.emit(type), [menuManager]);
  }

  function useMenu(options: {
    leftMenuItems?: MenuItem<MenuT>[];
    rightMenuItems?: MenuItem<MenuT>[];
    onSelectMenuItem?: (type: MenuT) => void;
  }) {
    const { leftMenuItems, rightMenuItems, onSelectMenuItem } = options;

    useSetLeftMenuItems(leftMenuItems);
    useSetRightMenuItems(rightMenuItems);

    const noop = useCallback(() => {}, []);
    useHandleMenuItem(onSelectMenuItem ?? noop);
  }

  return {
    Context: NoyaStateContext,
    Provider: NoyaStateProvider,
    NoyaContextProvider,
    useValue,
    useSetValue,
    useValueState,
    useStateManager,
    useSharedConnectionDataManager,
    useLeftMenuItems,
    useSetLeftMenuItems,
    useRightMenuItems,
    useSetRightMenuItems,
    useHandleMenuItem,
    useOnSelectMenuItemCallback,
    useNoyaManager,
    useMenu,
  };
}
